import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { ToastService } from 'src/app/shared/services/toast/toast.service'
import { IFieldConfig } from 'src/app/domain/IFieldConfig'
import { RESPONSE_CONFIG_FIELDS } from './faq-response-config'
import { COLUMNS_TO_SHOW_WEB } from '../crud-config'
import { AuthService } from 'src/app/views/auth/shared/auth.service'
import { FaqService } from '../crud-service'
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum'
import { IDynamicQuestion } from 'src/app/domain/registration/models/IDynamicQuestion'
import { IFaqData } from 'src/app/domain/registration/models/IFaqData'
import { atLeastOneResponseValidator } from './atLeastOneResponseValidator'

@Component({
  selector: 'app-faq-response-dialog',
  templateUrl: './faq-response-dialog.component.html',
  styleUrls: ['./faq-response-dialog.component.sass']
})
export class FaqResponseDialogComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator
  title: string = 'Responder ao Cliente'
  form: FormGroup
  config: IFieldConfig[] = RESPONSE_CONFIG_FIELDS
  displayedColumns = COLUMNS_TO_SHOW_WEB
  userName: string = ''
  userType: string = ''
  isEmailEditable: boolean = false
  typeCes: TypeCesEnum
  dynamicQuestions: IDynamicQuestion[] = []
  adminResponses: FormArray
  hasCategories: boolean = false
  categoryQuestions: IDynamicQuestion[] = []
  hasNormalQuestions: boolean = false
  isLoading: boolean = false
  Array = Array

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private faqService: FaqService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<FaqResponseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IFaqData
  ) {
    this.form = this.fb.group({
      email: [{ value: data.email, disabled: false }, [Validators.required, Validators.email]],
      adminResponses: this.fb.array([])
    });

    this.adminResponses = this.form.get('adminResponses') as FormArray;
    this.adminResponses.setValidators(atLeastOneResponseValidator);

    this.userName = this.authService.getUserName() || 'Atendente';
    this.userType = this.authService.isPartner() ? 'Parceiro' : 'Administrador';
  }

  ngOnInit(): void {
    this.initializeDynamicQuestions()
  }

  initializeDynamicQuestions(): void {
    console.log('📌 Dados recebidos no componente de resposta:', this.data);

    if (!this.data) {
      console.error('Erro: Nenhum dado foi passado para o diálogo de resposta.');
      this.toastService.showError('Erro ao carregar dados da resposta.');
      return;
    }

    if (!this.data.answers || !Array.isArray(this.data.answers)) {
      console.error('Erro: O campo answers não existe ou não é um array:', this.data.answers);
      this.data.answers = []; // Inicializar como array vazio para evitar erros
      this.toastService.showError('Não foi possível carregar as perguntas do usuário.');
      return;
    }

    console.log(`📌 Total de respostas recebidas: ${this.data.answers.length}`);

    // Processar todos os itens para identificar categorias e perguntas
    const processedQuestions = this.data.answers
      .filter(a => {
        if (!a || !a.question) {
          console.warn('Encontrado item inválido no array de respostas:', a);
          return false;
        }

        const q = a.question.toLowerCase();
        // Excluir perguntas "Nome", "E-mail", "Instagram", "Empresa" e "Telefone"
        const isProfileField = q === 'nome' || q === 'e-mail' || q === 'email' ||
          q === 'instagram' || q === 'empresa' || q === 'telefone';

        return !isProfileField;
      })
      .map(a => {
        // Processar a resposta com base no seu tipo
        let formattedAnswer = '';
        let isCheckbox = false;

        // Verificar se é um checkbox baseado em padrões comuns
        // 1. Verifica se tem os valores típicos de checkbox (Sim/Não, true/false)
        // 2. Verifica se é uma opção categórica como "Jurídico", "Consultor"
        const checkboxKeywords = ['jurídico', 'consultor', 'financeiro', 'comercial',
          'administrativo', 'marketing', 'rh', 'recursos humanos'];

        const answerStr = String(a.answer).toLowerCase();
        const questionStr = a.question.toLowerCase();

        // Detectar checkboxes com base no conteúdo
        isCheckbox =
          // Se a resposta for 'sim'/'não' em questões categóricas
          ((answerStr === 'sim' || answerStr === 'não' || answerStr === 'true' || answerStr === 'false')
            && checkboxKeywords.some(keyword => questionStr.includes(keyword)))
          // Ou se a pergunta for uma das categorias comuns
          || checkboxKeywords.some(keyword => questionStr === keyword);

        // Verificar se é um array (checkboxes/múltipla escolha)
        if (Array.isArray(a.answer)) {
          isCheckbox = true;
          formattedAnswer = a.answer.join(', ');
        } else if (typeof a.answer === 'boolean') {
          isCheckbox = true;
          formattedAnswer = a.question; // Usar o texto da questão em vez de Sim/Não
        } else if (answerStr === 'sim' && isCheckbox) {
          // Para checkboxes com resposta "sim", mostrar o nome da categoria
          formattedAnswer = a.question;
        } else {
          // Outros tipos (texto, etc)
          formattedAnswer = a.answer ? a.answer.toString() : '';
        }

        console.log(`Processando questão: "${a.question}" - Resposta: "${formattedAnswer}" - isCheckbox: ${isCheckbox}`);

        return {
          question: a.question,
          answer: formattedAnswer,
          isCheckbox: isCheckbox,
          showResponse: false,
          originalAnswer: a.answer // Preservar a resposta original
        };
      });

    // Separar em duas categorias: assuntos (checkbox) e perguntas normais
    const categories = processedQuestions.filter(q => q.isCheckbox &&
      (q.originalAnswer === true ||
        q.originalAnswer === 'true' ||
        q.originalAnswer === 'sim' ||
        q.originalAnswer === 'Sim' ||
        (Array.isArray(q.originalAnswer) && q.originalAnswer.length > 0)));

    const normalQuestions = processedQuestions.filter(q => !q.isCheckbox);

    // Atualizar as propriedades de controle
    this.categoryQuestions = categories;
    this.hasCategories = categories.length > 0;
    this.hasNormalQuestions = normalQuestions.length > 0;

    // Combinar os dois arrays, com categorias primeiro
    this.dynamicQuestions = [...categories, ...normalQuestions];

    console.log('📌 Perguntas processadas para exibição:', this.dynamicQuestions);
    console.log('📌 Assuntos/Categorias:', categories.length);
    console.log('📌 Perguntas normais:', normalQuestions.length);

    // Criar controles de formulário para cada pergunta
    this.dynamicQuestions.forEach(() => {
      this.adminResponses.push(this.fb.control(''));
    });

    if (this.dynamicQuestions.length === 0) {
      console.warn('Nenhuma pergunta disponível para resposta após o processamento.');
      this.toastService.showWarn('Nenhuma pergunta disponível para resposta.');
    }
  }

  // Método para mostrar/ocultar o campo de resposta
  toggleResponse(index: number): void {
    if (!this.dynamicQuestions[index]) return;

    this.dynamicQuestions[index].showResponse = !this.dynamicQuestions[index].showResponse;

    // Se estiver ocultando a resposta, limpa o campo
    if (!this.dynamicQuestions[index].showResponse) {
      if (this.adminResponses.controls[index]) {
        this.adminResponses.controls[index].setValue('');
      }
    }
  }

  get adminResponsesControls() {
    return this.adminResponses.controls
  }

  public getFieldConfig(name: string): any {
    return this.config.find(c => c.name === name)
  }

  toggleEmailEdit(): void {
    this.isEmailEditable = !this.isEmailEditable
    if (this.isEmailEditable) {
      this.form.get('email')?.enable()
    } else {
      this.form.get('email')?.disable()
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.toastService.showError('Por favor, preencha todos os campos corretamente.');
      return;
    }

    this.isLoading = true;

    // Filtrar apenas as perguntas respondidas (ignorando categorias)
    const answeredResponses = this.adminResponses.controls
      .map((ctrl, index) => ({
        question: this.dynamicQuestions[index].question,
        response: ctrl.value,
        isCheckbox: this.dynamicQuestions[index].isCheckbox,
        originalAnswer: this.dynamicQuestions[index].originalAnswer,
        userQuestion: !this.dynamicQuestions[index].isCheckbox // Apenas perguntas não-checkbox são perguntas do usuário
      }))
      .filter(item => !item.isCheckbox && item.response && item.response.trim() !== '');

    // Obter todas as categorias, independente de resposta (elas não são respondíveis)
    const categories = this.dynamicQuestions
      .filter(q => q.isCheckbox)
      .map(q => ({
        question: q.question,
        response: '', // Categorias não têm resposta
        isCheckbox: true,
        originalAnswer: q.originalAnswer
      }));

    // Se não há respostas para perguntas normais, mostrar erro
    if (answeredResponses.length === 0) {
      this.toastService.showError('Por favor, responda pelo menos uma pergunta.');
      this.isLoading = false;
      return;
    }

    // Todas as respostas para envio (categorias + respostas)
    const allResponses = [...categories, ...answeredResponses];

    // Formatação para o email - assuntos primeiro
    let formattedCategories = '';
    if (categories.length > 0) {
      formattedCategories = '<strong style="font-size: 16px;">ASSUNTOS SELECIONADOS:</strong><br><ul style="margin-top: 5px; margin-bottom: 15px;">' +
        categories.map(cat => `<li>${cat.question}</li>`).join('') +
        '</ul>';
    }

    // Formatação para perguntas e respostas
    const formattedQuestions = answeredResponses.map(item => {
      return `<div style="margin-bottom: 20px;">
        <strong style="font-size: 16px;">CAMPO DO FORMULÁRIO:</strong> ${item.question}<br>
        <strong>Resposta do cliente:</strong> ${item.originalAnswer || '(Não informada)'}<br>
        <strong>Nossa resposta:</strong> ${item.response}
      </div>`;
    }).join('');

    // Combinação de categorias + perguntas/respostas
    const emailContent = formattedCategories + formattedQuestions;

    const responseData = {
      email: this.form.get('email')?.value || this.data.email,
      question: emailContent, // Conteúdo completo já formatado
      response: '', // Não usaremos o campo response separadamente
      responder: this.userName,
      answers: allResponses,
      responderType: this.userType || 'Atendente',
      date: new Date().toLocaleString()
    }

    console.log('📌 Dados da resposta para envio:', responseData);

    this.faqService.sendResponseEmail(responseData).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.showSuccess('Resposta enviada com sucesso!');
        this.dialogRef.close(responseData);
      },
      error: err => {
        this.isLoading = false;
        console.error('Erro ao enviar resposta:', err);
        this.toastService.showError('Erro ao enviar resposta. Tente novamente.');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close()
  }
}
